import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Modal, Spinner, Button, Alert, Form, Row, Col } from 'react-bootstrap';
import { BookOpen, AlertTriangle, CheckCircle, Plus, Trash2, Calendar, FileText, Lightbulb } from 'lucide-react';
import Select from 'react-select';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import * as api from '../../../../services/api/expensesApi';
import { resolveRoleAccount } from '../../../../services/api/chartRolesApi';
import * as tmplApi from '../../../../services/api/accountingTemplatesApi';
import { classifyAssetText, inlineAssetCategoryOptions, round2 } from '../../Accounting/Assets/assetUtils';
import { verifikatTableCss } from './verifikatTableStyles';

/**
 * BookExpenseModal
 *
 * Approver-facing preview + commit for booking an approved expense. Three
 * booking sources:
 *
 *   1. "Från kategori" (default) — backend chooses BAS account from
 *      expense.category and splits VAT to 2641 / credits 2891.
 *   2. "Från mall" — user picks a tenant/shared template. Frontend calls
 *      /templates/:id/suggestion with {gross, net, vat, vatRate, description}
 *      parameters and passes the resolved rows to /book.
 *   3. "Manuell" — user types the debit/credit rows directly.
 *
 * The backend's /book endpoint is the single write path. Template + Manual
 * share the same row-shape and the same semantic checks (balance +
 * payable-class credit totalling gross) — the only difference is whether a
 * templateId is also sent.
 */
const fmt = (n) => new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const fmtSek = (n) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(Number(n) || 0);


/**
 * Book-as-asset row derivation for the expense preview/booking (exported for
 * tests, re-review RRR5/RR3): the cost debit swaps to the category booking
 * account; for personbil (no input-VAT deduction — Skatteverket, Bilar och
 * moms) the 264x debits are removed and FOLDED INTO the capitalized asset
 * debit, so the posted debit equals the VAT-inclusive register basis.
 */
export const deriveAssetExpenseRows = (rows, { account, label, personbil }) => {
  const blockedVat = personbil
    ? rows
      .filter((r) => /^264/.test(String(r.account)) && Number(r.debit) > 0)
      .reduce((sum, r) => sum + Number(r.debit), 0)
    : 0;
  let swapped = false;
  return rows
    .filter((r) => !(personbil && /^264/.test(String(r.account)) && Number(r.debit) > 0))
    .map((r) => {
      // The cost row is the (first) debit row that isn't ingående moms (264x).
      if (!swapped && Number(r.debit) > 0 && !/^264/.test(String(r.account))) {
        swapped = true;
        return { ...r, account, debit: round2(Number(r.debit) + blockedVat), label };
      }
      return r;
    });
};

const ASSET_YEARS_OPTIONS = ['3', '4', '5', '7', '10'];

const BookExpenseModal = ({ companyId, expenseId, show, onClose, onBooked }) => {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [catPreview, setCatPreview] = useState(null);
  const [tmplPreview, setTmplPreview] = useState(null);
  const [expense, setExpense] = useState(null);
  const [error, setError] = useState(null);
  // The input-VAT display row's account (vat_incoming_domestic chart role).
  const [vatRoleLeg, setVatRoleLeg] = useState(null);
  useEffect(() => {
    if (!companyId) return undefined;
    let active = true;
    resolveRoleAccount(companyId, 'vat_incoming_domestic').then((leg) => {
      if (active) setVatRoleLeg(leg);
    });
    return () => { active = false; };
  }, [companyId]);
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState('category');
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [manualRows, setManualRows] = useState([]);
  // Fixed-asset interception (book/preview → assetSuggestion). When the
  // backend flags an equipment expense at/over the halvt-prisbasbelopp
  // threshold, the modal defaults to booking it as a tillgång: the cost-
  // account debit row is swapped for the category's tillgångskonto and the
  // register asset is created atomically with the verifikat (asAsset).
  const [assetSuggestion, setAssetSuggestion] = useState(null);
  const [assetChoice, setAssetChoice] = useState('asset'); // 'asset' | 'expense' (korttidsinventarie opt-out)
  const [assetCategoryCode, setAssetCategoryCode] = useState('computers');
  const [assetVehicleType, setAssetVehicleType] = useState('personbil'); // vehicles/k1_vehicles: personbil | lastbil
  const [assetYears, setAssetYears] = useState('5');
  const [assetName, setAssetName] = useState('');
  // Tenant asset_categories rows (for asset_account numbers). Empty until
  // fetched — a suggestion triggers the fetch; ASSET_CATEGORIES + the seed
  // account map act as fallback if the endpoint fails.
  const [assetCategories, setAssetCategories] = useState([]);
  // Flat list of the tenant's kontoplan, formatted for react-select.
  // Matches the pattern in Bookkeeping/ManualBookkeepingEntry — no typeahead,
  // just a sorted dropdown ordered by account number.
  const [accounts, setAccounts] = useState([]);
  // Fast number→name lookup so preview rows display the tenant's real
  // account names (not backend-invented labels).
  const nameFor = (num) => {
    const a = accounts.find((x) => x.value === String(num));
    return a ? a.accountName : '';
  };
  // Inline metadata overrides — the user can complete missing VAT /
  // distance / participants directly in the preview area. These patch the
  // preview live (debounced re-fetch) and are persisted on the expense
  // when the user clicks Bokför. No separate save step.
  const [overrides, setOverrides] = useState({});
  // Verifikat header fields — date + description. Admin-editable because
  // the expense date might need a correction (receipt was submitted late)
  // and the auto-generated "Utlägg: …" description often benefits from
  // motpart / invoice-number context. Initialized from the preview load.
  const [verifikatDate, setVerifikatDate] = useState('');
  const [verifikatDescription, setVerifikatDescription] = useState('');

  // Reset on open/close.
  useEffect(() => {
    if (!show) {
      setCatPreview(null); setTmplPreview(null); setExpense(null);
      setError(null); setSource('category'); setTemplateId('');
      setManualRows([]);
      setOverrides({});
      setVerifikatDate(''); setVerifikatDescription('');
      setAssetSuggestion(null); setAssetChoice('asset');
      setAssetCategoryCode('computers'); setAssetYears('5'); setAssetName('');
      setAssetVehicleType('personbil');
      setAssetCategories([]);
      return;
    }
  }, [show]);

  // Load category preview + template list on open. Running both in parallel
  // so switching to template mode is instant.
  useEffect(() => {
    if (!show || !expenseId) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingPreview(true); setLoadingTemplates(true); setError(null);
      try {
        const [previewRes, tmplRes, accountsRes] = await Promise.all([
          api.previewExpenseBooking(companyId, expenseId),
          tmplApi.listTemplates(companyId).catch(() => ({ data: { templates: [] } })),
          // Kontoplan fetch — mirror ManualBookkeepingEntry's shape so the
          // react-select options render identically across the app.
          axios.get(getApiUrl(`${companyId}/accounting/accounts`), { withCredentials: true })
            .catch(() => ({ data: { accounts: [] } })),
        ]);
        if (cancelled) return;
        setCatPreview(previewRes.data?.preview || null);
        setExpense(previewRes.data?.expense || null);
        // Fixed-asset suggestion — seed the panel defaults from the prefill
        // so "book as asset" is one click when the backend recommends it.
        const sug = previewRes.data?.assetSuggestion || null;
        setAssetSuggestion(sug);
        setAssetChoice('asset');
        setAssetCategoryCode('computers');
        setAssetYears('5');
        setAssetName(sug?.prefill?.name || previewRes.data?.expense?.description || '');
        // Pre-seed date + description from the preview so the Bokför form
        // isn't empty and the user can tweak from a sensible default.
        setVerifikatDate(previewRes.data?.preview?.date || previewRes.data?.expense?.expense_date || '');
        setVerifikatDescription(previewRes.data?.preview?.description || '');
        // Pre-seed a default VAT rate so if the user types an amount without
        // touching the dropdown (which already shows 25% visually), the rate
        // state exists and canBook no longer blocks. Only seed when the
        // expense truly has no VAT on it; don't clobber existing values.
        const ex = previewRes.data?.expense;
        if (ex && (ex.metadata?.vat?.amount == null || ex.metadata?.vat?.rate == null)) {
          setOverrides((o) => ({ ...o, vatRate: o.vatRate || '25' }));
        }
        const list = Array.isArray(tmplRes.data?.templates) ? tmplRes.data.templates
                   : Array.isArray(tmplRes.data) ? tmplRes.data : [];
        setTemplates(list);
        const rawAccounts = Array.isArray(accountsRes.data?.accounts) ? accountsRes.data.accounts : [];
        const fmtAccounts = rawAccounts
          .filter((a) => a.is_active !== false)
          .map((a) => ({
            value: String(a.account_number),
            label: `${a.account_number} - ${a.account_name}`,
            accountName: a.account_name,
          }))
          .sort((a, b) => a.value.localeCompare(b.value));
        setAccounts(fmtAccounts);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Kunde inte hämta förhandsvisning.');
      } finally {
        if (!cancelled) { setLoadingPreview(false); setLoadingTemplates(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [show, companyId, expenseId]);

  // Tenant asset categories — fetched lazily, only when a capitalize
  // suggestion is on screen (no extra request for ordinary expenses).
  // Failure is non-fatal: the panel falls back to ASSET_CATEGORIES + the
  // seed account map.
  useEffect(() => {
    if (!show || assetSuggestion?.suggestion !== 'capitalize') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(getApiUrl(`${companyId}/assets/categories`), { withCredentials: true });
        const data = res.data?.data ?? res.data;
        const rows = Array.isArray(data) ? data : data?.categories;
        if (!cancelled && Array.isArray(rows) && rows.length > 0) setAssetCategories(rows);
      } catch { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, [show, companyId, assetSuggestion]);

  const runSuggestion = useCallback(async (tid) => {
    if (!tid || !expense) { setTmplPreview(null); return; }
    setLoadingSuggestion(true);
    setTmplPreview(null);
    try {
      const gross = Number(expense.amount) || 0;
      const vat   = Number(expense?.metadata?.vat?.amount ?? 0);
      const vatRate = Number(expense?.metadata?.vat?.rate ?? 0);
      const net   = Math.max(0, gross - vat);
      const parameters = {
        gross: gross.toFixed(2),
        net:   net.toFixed(2),
        vat:   vat.toFixed(2),
        vatRate: String(vatRate),
        amount: gross.toFixed(2),
      };
      const res = await tmplApi.suggestFromTemplate(companyId, tid, parameters, {});
      const rows = (res.data?.suggestedTransactions || []).map((r) => ({
        account_number: r.account_number,
        account_name: r.account_name,
        debit: r.debit ? parseFloat(r.debit) : 0,
        credit: r.credit ? parseFloat(r.credit) : 0,
      }));
      const totalDebit = rows.reduce((s, r) => s + r.debit, 0);
      const totalCredit = rows.reduce((s, r) => s + r.credit, 0);
      const warnings = [];
      if (Math.abs(totalCredit - gross) > 0.01) {
        warnings.push(`Mallens kreditsumma (${fmt(totalCredit)}) matchar inte utläggets belopp (${fmt(gross)}).`);
      }
      setTmplPreview({
        rows,
        totals: {
          debit: totalDebit, credit: totalCredit,
          gross, net, vat,
        },
        balanced: Math.abs(totalDebit - totalCredit) < 0.01,
        warnings,
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte hämta mallförslag.');
    } finally {
      setLoadingSuggestion(false);
    }
  }, [companyId, expense]);

  const onTemplateChange = (tid) => {
    setTemplateId(tid);
    if (tid) runSuggestion(tid);
    else setTmplPreview(null);
  };

  // Seed the manual editor from the category preview the first time the
  // user switches to "Manuell" — editing is faster than starting blank.
  useEffect(() => {
    if (source === 'manual' && manualRows.length === 0 && catPreview?.rows?.length) {
      setManualRows(catPreview.rows.map((r) => ({
        account_number: r.account,
        label: r.label || '',
        debit:  r.debit  || 0,
        credit: r.credit || 0,
      })));
    }
  }, [source, catPreview, manualRows.length]);

  // Local preview for manual mode — same shape as tmplPreview so the render
  // path stays unified. Recomputed whenever the user edits a row.
  const manualPreview = (() => {
    if (source !== 'manual' || !expense) return null;
    const gross = Number(expense.amount) || 0;
    const vat   = Number(expense?.metadata?.vat?.amount ?? 0);
    const net   = Math.max(0, gross - vat);
    const rows  = manualRows.filter((r) => r.account_number && (Number(r.debit) > 0 || Number(r.credit) > 0));
    const totalDebit  = rows.reduce((s, r) => s + (Number(r.debit)  || 0), 0);
    const totalCredit = rows.reduce((s, r) => s + (Number(r.credit) || 0), 0);
    const balanced = rows.length >= 2 && Math.abs(totalDebit - totalCredit) < 0.01;
    const warnings = [];
    if (rows.length < 2) warnings.push('Minst en debet- och en kreditrad krävs.');
    if (rows.length >= 2 && !balanced) warnings.push(`Verifikatet balanserar inte (D ${fmt(totalDebit)} / K ${fmt(totalCredit)}).`);
    if (rows.length >= 2 && Math.abs(totalCredit - gross) > 0.01) {
      warnings.push(`Kreditsumma (${fmt(totalCredit)}) måste matcha utläggets belopp (${fmt(gross)}).`);
    }
    return {
      rows: rows.map((r) => ({
        account: String(r.account_number),
        debit:  Number(r.debit)  || 0,
        credit: Number(r.credit) || 0,
        label:  r.label || '',
      })),
      totals: { debit: totalDebit, credit: totalCredit, gross, net, vat },
      balanced: warnings.length === 0,
      warnings,
    };
  })();

  // Asset-panel derived state. The panel (and the row swap) only applies to
  // category-source bookings — template/manual behave exactly as before.
  // Tenant rows win over the bundled fallback list. The cost-row debit swaps
  // to the category's BOOKING account: on K1 pool classes that is the year's
  // acquisition account (12x1 — BFNAR 2006:1/BAS 2018 K1), never the base
  // account (review remediation F3). K1 property/land classes are excluded
  // from this inline flow — they need the tax classification and belong in
  // the full asset form.
  const assetCategoryList = inlineAssetCategoryOptions(assetCategories);
  const selectedAssetCategory =
    assetCategoryList.find((c) => c.code === assetCategoryCode) || assetCategoryList[0];
  const assetActive =
    source === 'category' && assetSuggestion?.suggestion === 'capitalize' && assetChoice === 'asset';

  const assetIsVehicle = ['vehicles', 'k1_vehicles'].includes(selectedAssetCategory?.code);
  const assetPersonbil = assetIsVehicle && assetVehicleType === 'personbil';

  // Category preview with the cost-account debit row swapped for the chosen
  // category's tillgångskonto (same net amount). Personbil (re-review RR3):
  // no input-VAT deduction is allowed (Skatteverket, Bilar och moms) — the
  // 264x debit is FOLDED INTO the capitalized asset debit instead of being
  // lifted; totals/balance carry over unchanged. Otherwise VAT rows are
  // untouched. These rows are both what the preview table shows and what
  // /book receives.
  const assetPreview = (!assetActive || !catPreview?.rows?.length || !selectedAssetCategory)
    ? null
    : {
      ...catPreview,
      rows: deriveAssetExpenseRows(catPreview.rows, {
        account: selectedAssetCategory.asset_account,
        label: nameFor(selectedAssetCategory.asset_account) || selectedAssetCategory.label,
        personbil: assetPersonbil,
      }),
    };

  const activePreview =
      source === 'template' ? tmplPreview
    : source === 'manual'   ? manualPreview
    : (assetPreview || catPreview);

  // What's still missing for a legal booking — drives inline inputs and
  // blocks the Bokför button until filled in. Needs to be declared BEFORE
  // canBook references it (temporal-dead-zone gotcha).
  const missing = (() => {
    if (!expense) return {};
    const eff = {
      vatAmount: overrides.vatAmount ?? expense.metadata?.vat?.amount,
      vatRate:   overrides.vatRate   ?? expense.metadata?.vat?.rate,
      distance_km: overrides.distance_km ?? expense.metadata?.distance_km,
      participants: overrides.participants ?? expense.metadata?.participants,
    };
    return {
      vat: eff.vatAmount == null || eff.vatRate == null,
      mileage: expense.category === 'mileage' && (eff.distance_km == null || Number(eff.distance_km) <= 0),
      participants: expense.category === 'representation' && (eff.participants == null || Number(eff.participants) < 1),
    };
  })();
  const anyMissing = missing.vat || missing.mileage || missing.participants;

  const canBook = !!activePreview && activePreview.balanced && !error
    && (source !== 'template' || (templateId && !loadingSuggestion))
    // Block category bookings that still have missing compliance fields.
    && !(source === 'category' && anyMissing);

  // Rows to render in the preview table. For category mode with missing
  // VAT we inject an editable input-VAT row (the tenant's
  // vat_incoming_domestic chart role) between the last cost row and the
  // first credit row so the user can type the VAT amount + rate right
  // where it belongs on the verifikat. The typed value is debounced back
  // to the server via previewBooking?vatAmount=...&vatRate=... which
  // recomputes the cost row split (the server resolves its own role account
  // when it books — this row is display/entry only).
  const displayRows = (() => {
    const baseRows = activePreview?.rows || [];
    if (source !== 'category' || !missing.vat || !expense) return baseRows;
    // Inject before the first credit-side row.
    const insertAt = baseRows.findIndex((r) => Number(r.credit) > 0);
    const placeholder = {
      account: (vatRoleLeg && vatRoleLeg.accountNumber) || '',
      label: 'Ingående moms',
      debit: 0,
      credit: 0,
      _editable: 'vat',
    };
    const result = baseRows.slice();
    if (insertAt >= 0) result.splice(insertAt, 0, placeholder);
    else result.push(placeholder);
    return result;
  })();

  // Recovery: if the backend rejects /book with a missing-metadata error,
  // re-fetch /book/preview so the inline editors re-appear over the table.
  const reopenForMissingMetadata = async () => {
    try {
      const res = await api.previewExpenseBooking(companyId, expense.expense_id);
      setExpense(res.data?.expense || null);
      setCatPreview(res.data?.preview || null);
    } catch { /* surface the original error */ }
  };

  const handleBook = async () => {
    setBusy(true); setError(null);
    try {
      let body;
      if (source === 'template') {
        body = {
          source: 'template',
          templateId,
          rows: tmplPreview.rows.map((r) => ({
            account_number: r.account_number,
            debit: r.debit, credit: r.credit,
            label: r.account_name || '',
          })),
        };
      } else if (source === 'manual') {
        body = {
          source: 'manual',
          rows: manualPreview.rows.map((r) => ({
            account_number: r.account,
            debit: r.debit, credit: r.credit,
            label: r.label || '',
          })),
        };
      } else if (assetActive && assetPreview) {
        // Book-as-asset: the swapped rows (tillgångskonto debit) go up as a
        // manual booking + asAsset, and the backend creates the register
        // asset atomically with the verifikat.
        const assetRow = assetPreview.rows.find(
          (r) => String(r.account) === selectedAssetCategory.asset_account && Number(r.debit) > 0,
        );
        body = {
          source: 'manual',
          rows: assetPreview.rows.map((r) => ({
            account_number: String(r.account),
            debit: Number(r.debit) || 0,
            credit: Number(r.credit) || 0,
            label: r.label || '',
          })),
          asAsset: {
            category_code: selectedAssetCategory.code,
            acquisition_value: Number(assetRow?.debit) || 0,
            useful_months: (parseInt(assetYears, 10) || 5) * 12,
            name: (assetName || '').trim() || undefined,
            // The vehicle VAT fact is ALWAYS explicit (re-review RR3):
            // personbil = no input-VAT deduction, blocked VAT folded into
            // the capitalized value above; lastbil/arbetsfordon = standard
            // (deductible at purchase, output VAT at sale).
            ...(assetIsVehicle && {
              vat_profile: assetVehicleType === 'lastbil' ? 'standard' : 'personbil',
            }),
          },
        };
      } else {
        body = { source: 'category' };
      }
      // If the user typed inline metadata (missing VAT on the 2641 row,
      // missing distance/participants), send it as an override. Backend
      // merges it into the expense before the verifikat is posted AND
      // persists the completed metadata on the expense row.
      const mdOverride = {};
      if (overrides.vatAmount != null && overrides.vatAmount !== '') {
        mdOverride.vat = {
          amount: parseFloat(overrides.vatAmount) || 0,
          rate: parseInt(overrides.vatRate, 10) || 0,
        };
      }
      if (overrides.distance_km != null && overrides.distance_km !== '') {
        mdOverride.distance_km = parseFloat(overrides.distance_km) || 0;
      }
      if (overrides.vehicle_type) mdOverride.vehicle_type = overrides.vehicle_type;
      if (overrides.participants != null && overrides.participants !== '') {
        mdOverride.participants = parseInt(overrides.participants, 10) || 0;
      }
      if (overrides.meal_type) mdOverride.meal_type = overrides.meal_type;
      if (Object.keys(mdOverride).length > 0) body.metadataOverride = mdOverride;
      // Admin-edited verifikat header (only send when non-empty to let the
      // backend fall back to its auto-generated defaults otherwise).
      if (verifikatDate) body.date = verifikatDate;
      if (verifikatDescription && verifikatDescription.trim()) body.description = verifikatDescription.trim();
      const res = await api.bookExpense(companyId, expenseId, body);
      if (res.data?.assetIds) {
        toast.success('Utlägget är bokfört och tillgången är upplagd — avskrivningen sköts automatiskt.');
      } else {
        toast.success(`Bokfört ${res.data?.series || 'V'}${res.data?.verificationNumber}.`);
      }
      onBooked?.(res.data);
    } catch (err) {
      const msg = err.response?.data?.message || 'Bokföring misslyckades.';
      setError(msg);
      setBusy(false);
      // If the backend reported missing metadata (VAT / distance / participants),
      // recover by re-fetching the preview so the inline form appears.
      if (/VAT amount|distance_km|participants/i.test(msg)) {
        await reopenForMissingMetadata();
      }
    }
  };

  // Flatten overrides into query params for the preview endpoint.
  const overrideParams = (() => {
    const p = {};
    if (overrides.vatAmount != null && overrides.vatAmount !== '') p.vatAmount = overrides.vatAmount;
    if (overrides.vatRate != null && overrides.vatRate !== '')     p.vatRate   = overrides.vatRate;
    if (overrides.distance_km != null && overrides.distance_km !== '') p.distance_km = overrides.distance_km;
    if (overrides.vehicle_type) p.vehicle_type = overrides.vehicle_type;
    if (overrides.participants != null && overrides.participants !== '') p.participants = overrides.participants;
    if (overrides.meal_type) p.meal_type = overrides.meal_type;
    return p;
  })();
  const overrideKey = JSON.stringify(overrideParams);

  // Debounced re-preview whenever the user edits an override. Keeps the
  // rows table + totals live without a save button.
  useEffect(() => {
    if (!show || !expense || Object.keys(overrideParams).length === 0) return undefined;
    const t = setTimeout(async () => {
      try {
        const res = await api.previewExpenseBooking(companyId, expense.expense_id, overrideParams);
        setCatPreview(res.data?.preview || null);
        // Overrides change the net amount, which can move the expense across
        // the halvt-prisbasbelopp threshold — keep the asset panel in sync.
        const sug = res.data?.assetSuggestion ?? null;
        setAssetSuggestion(sug);
        if (sug?.suggestion === 'capitalize') {
          setAssetName((n) => n || sug.prefill?.name || expense.description || '');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Kunde inte uppdatera förhandsvisning.');
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideKey, show, companyId, expense?.expense_id]);

  // AI classification prefill (A3) — once per expense, only after the
  // deterministic detector said "capitalize". Conservative apply: only
  // fields still at their defaults are touched. classifyAssetText returns
  // null on 402/403 (no plan feature / no credits) or any error, and the
  // deterministic prefill stands (credit-aware degradation).
  const classifiedExpenseRef = useRef(null);
  useEffect(() => {
    if (!show || !expense || assetSuggestion?.suggestion !== 'capitalize') return undefined;
    if (classifiedExpenseRef.current === expense.expense_id) return undefined;
    classifiedExpenseRef.current = expense.expense_id;
    let cancelled = false;
    (async () => {
      const text = [
        expense.description && `Beskrivning: ${expense.description}`,
        expense.merchant && `Inköpsställe: ${expense.merchant}`,
        assetSuggestion?.prefill?.amount != null && `Belopp exkl. moms: ${assetSuggestion.prefill.amount} kr`,
      ].filter(Boolean).join('\n');
      const c = await classifyAssetText(companyId, text);
      if (cancelled || !c || (c.confidence || 0) < 0.5) return;
      setAssetCategoryCode((code) => (code === 'computers' ? c.category_code : code));
      if (c.useful_months) {
        setAssetYears((y) => (y === '5' ? String(Math.round(c.useful_months / 12)) : y));
      }
      if (c.name) {
        setAssetName((n) => {
          const deterministic = assetSuggestion?.prefill?.name || expense.description || '';
          return !n || n === deterministic ? c.name : n;
        });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, expense?.expense_id, assetSuggestion, companyId]);

  // Manual row editor helpers
  const addManualRow = () => setManualRows((rows) => [
    ...rows,
    { account_number: '', label: '', debit: 0, credit: 0 },
  ]);
  const updateManualRow = (idx, patch) => setManualRows((rows) =>
    rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
  );
  const removeManualRow = (idx) => setManualRows((rows) => rows.filter((_, i) => i !== idx));

  return (
    <Modal show={show} onHide={busy ? undefined : onClose} size="lg" centered backdrop={busy ? 'static' : true}>
      <Modal.Header closeButton={!busy}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <BookOpen size={18} /> Bokför utlägg
          </div>
          {expense && (
            <div style={{ marginTop: 4, fontSize: '0.75rem', color: '#64748b', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 380 }} title={expense.description}>
                {expense.description || '—'}
              </span>
              <span>·</span>
              <span style={{ fontWeight: 600, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                {fmtSek(Number(expense.amount) || 0)}
              </span>
              {expense.employee_firstname && (
                <>
                  <span>·</span>
                  <span>{expense.employee_firstname} {expense.employee_lastname || ''}</span>
                </>
              )}
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body>
        {loadingPreview && (
          <div className="text-center py-4"><Spinner animation="border" /></div>
        )}

        {!loadingPreview && error && (
          <Alert variant="danger" className="mb-3"><AlertTriangle size={14} className="me-1" /> {error}</Alert>
        )}

        {/* When the backend can't yet build a preview (stale API that
            returned preview:null + requiresMetadata, or a truly incomplete
            expense) we still need to show SOMETHING so the user can fill
            it in. Render header + inline editors without the rows table. */}
        {!loadingPreview && !catPreview && expense && (
          <div style={{ marginBottom: 12, fontSize: 13, color: '#374151' }}>
            <div><strong>Datum:</strong> {expense.expense_date}</div>
            <div><strong>Beskrivning:</strong> {expense.description || '—'}</div>
            <div><strong>Belopp:</strong> {fmt(Number(expense.amount) || 0)} {expense.currency || 'SEK'}</div>
            <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>
              <AlertTriangle size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
              Kompletterande uppgifter behövs innan förhandsvisningen kan byggas.
            </div>
          </div>
        )}

        {/* Mileage + participants can't be inlined in the rows because
            filling them reshapes which accounts appear (7331/7332 split,
            6071/6072/6073 etc.). Keep those two as a compact pre-table
            editor. VAT is handled inline in the 2641 row below. */}
        {!loadingPreview && expense && source === 'category' && (missing.mileage || missing.participants) && (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, marginBottom: 12, fontSize: 13 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={12} /> Fyll i för att bestämma konton
            </div>
            {missing.mileage && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                <Form.Control
                  type="number" step="1" min="0" size="sm"
                  placeholder="Sträcka (km)"
                  value={overrides.distance_km || ''}
                  onChange={(e) => setOverrides({ ...overrides, distance_km: e.target.value })}
                />
                <Form.Select
                  size="sm"
                  value={overrides.vehicle_type || 'own'}
                  onChange={(e) => setOverrides({ ...overrides, vehicle_type: e.target.value })}
                >
                  <option value="own">Egen bil (25 kr/mil)</option>
                  <option value="company_gas">Tjänstebil bensin/diesel (12 kr/mil)</option>
                  <option value="company_diesel">Tjänstebil diesel (12 kr/mil)</option>
                  <option value="company_ev">Tjänstebil el (9,50 kr/mil)</option>
                </Form.Select>
              </div>
            )}
            {missing.participants && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                <Form.Control
                  type="number" step="1" min="1" size="sm"
                  placeholder="Antal personer"
                  value={overrides.participants || ''}
                  onChange={(e) => setOverrides({ ...overrides, participants: e.target.value })}
                />
                <Form.Select
                  size="sm"
                  value={overrides.meal_type || ''}
                  onChange={(e) => setOverrides({ ...overrides, meal_type: e.target.value })}
                >
                  <option value="">Typ av representation…</option>
                  <option value="simple_refreshment">Enklare förtäring (fika)</option>
                  <option value="meal">Måltid</option>
                  <option value="gift">Representationsgåva</option>
                </Form.Select>
              </div>
            )}
          </div>
        )}

        {!loadingPreview && catPreview && (
          <>
            {/* ─── Fixed-asset interception (assetSuggestion: capitalize) ───
                Category-source only; template/manual are untouched. The
                opt-out wording is deliberate: above the halvt-prisbasbelopp
                threshold, direct expensing is only lawful for korttids-
                inventarier (ekonomisk livslängd ≤ 3 år, IL 18 kap. 4 §). */}
            {source === 'category' && assetSuggestion?.suggestion === 'capitalize' && (
              <div
                style={{
                  background: 'var(--rgba-primary-1)',
                                    borderRadius: 8,
                  padding: '0.875rem 1rem',
                  marginBottom: 16,
                }}
              >
                <div className="d-flex align-items-start gap-2">
                  <div
                    style={{
                      flexShrink: 0, width: 30, height: 30, borderRadius: 9,
                      background: '#fff', color: 'var(--primary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <Lightbulb size={16} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: 'var(--title)', fontSize: '0.9375rem' }}>
                      Det här ser ut som en tillgång
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--secondary)', marginTop: 2 }}>
                      {assetSuggestion.reason}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 10, fontSize: 13 }}>
                  <Form.Check
                    type="radio" id="asset-choice-asset"
                    label="Bokför som tillgång (rekommenderas)"
                    name="asset-choice" checked={assetChoice === 'asset'}
                    onChange={() => setAssetChoice('asset')}
                    disabled={busy}
                  />
                  <Form.Check
                    type="radio" id="asset-choice-expense"
                    label="Nej — den håller högst 3 år (korttidsinventarie, dras av direkt)"
                    name="asset-choice" checked={assetChoice === 'expense'}
                    onChange={() => setAssetChoice('expense')}
                    disabled={busy}
                  />
                </div>

                {assetChoice === 'asset' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1.3fr', gap: 8, marginTop: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>
                        Tillgångskategori
                      </div>
                      <Select
                        options={assetCategoryList.map((c) => ({ value: c.code, label: c.label }))}
                        value={{ value: selectedAssetCategory.code, label: selectedAssetCategory.label }}
                        onChange={(opt) => setAssetCategoryCode(opt ? opt.value : 'computers')}
                        isClearable={false}
                        isSearchable={false}
                        isDisabled={busy}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (base) => ({ ...base, minHeight: 31, fontSize: '0.8125rem', borderColor: '#e2e8f0' }),
                          option: (base) => ({ ...base, fontSize: '0.8125rem' }),
                          singleValue: (base) => ({ ...base, fontSize: '0.8125rem' }),
                        }}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>
                        Nyttjandeperiod
                      </div>
                      <Form.Select
                        size="sm" value={assetYears}
                        onChange={(e) => setAssetYears(e.target.value)}
                        disabled={busy}
                      >
                        {ASSET_YEARS_OPTIONS.map((y) => (
                          <option key={y} value={y}>{y} år</option>
                        ))}
                      </Form.Select>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>
                        5 år är standard enligt K2
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>
                        Benämning
                      </div>
                      <Form.Control
                        size="sm" type="text" maxLength={255}
                        value={assetName}
                        onChange={(e) => setAssetName(e.target.value)}
                        placeholder="T.ex. MacBook Pro 14”"
                        disabled={busy}
                      />
                    </div>
                  </div>
                )}

                {/* Fordon: personbil vs lastbil styr vat_profile (mirrors
                    CreateAssetForm/VerificationModal — F5): lastbil fick dra
                    av momsen, så en framtida försäljning bokförs med moms. */}
                {assetChoice === 'asset'
                  && ['vehicles', 'k1_vehicles'].includes(selectedAssetCategory?.code) && (
                  <div style={{ marginTop: 10, fontSize: 13 }}>
                    <Form.Check
                      type="radio"
                      id="exp-asset-vehicle-personbil"
                      name="exp-asset-vehicle-type"
                      label="Personbil — momsen fick inte dras av vid köpet, försäljning blir momsfri"
                      checked={assetVehicleType === 'personbil'}
                      onChange={() => setAssetVehicleType('personbil')}
                      disabled={busy}
                    />
                    <Form.Check
                      type="radio"
                      id="exp-asset-vehicle-lastbil"
                      name="exp-asset-vehicle-type"
                      label="Lastbil eller arbetsfordon — momsen drogs av, försäljning bokförs med moms"
                      checked={assetVehicleType === 'lastbil'}
                      onChange={() => setAssetVehicleType('lastbil')}
                      disabled={busy}
                    />
                  </div>
                )}

                {Number(expense?.amount) > 4000 && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 10 }}>
                    Kontrollera att kvittot uppfyller fakturakraven (företagets namn på fakturan)
                    — annars får momsen inte dras av.
                  </div>
                )}
              </div>
            )}

            {/* Verifikat header — matches ManualBookkeepingEntry layout
                (Row/Col, no wrapper box, icon-prefixed labels). */}
            <Row className="mb-4">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Calendar size={16} className="me-2" />
                    Datum
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={verifikatDate}
                    onChange={(e) => setVerifikatDate(e.target.value)}
                    disabled={busy}
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <FileText size={16} className="me-2" />
                    Beskrivning
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={verifikatDescription}
                    onChange={(e) => setVerifikatDescription(e.target.value)}
                    placeholder="Verifikatets beskrivning"
                    maxLength={255}
                    disabled={busy}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div style={{ background: '#f9fafb', padding: 10, borderRadius: 6, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 6 }}>
                Bokföringskälla
              </div>
              <div className="d-flex gap-3" style={{ fontSize: 13, flexWrap: 'wrap' }}>
                <Form.Check
                  type="radio" id="src-category" label="Från kategori (standard)"
                  name="booking-source" checked={source === 'category'}
                  onChange={() => setSource('category')}
                />
                <Form.Check
                  type="radio" id="src-template" label="Från mall"
                  name="booking-source" checked={source === 'template'}
                  onChange={() => setSource('template')}
                  disabled={loadingTemplates}
                />
                <Form.Check
                  type="radio" id="src-manual" label="Manuell"
                  name="booking-source" checked={source === 'manual'}
                  onChange={() => setSource('manual')}
                />
              </div>
              {source === 'template' && (
                <div style={{ marginTop: 8 }}>
                  <Form.Select
                    size="sm" value={templateId}
                    onChange={(e) => onTemplateChange(e.target.value)}
                    disabled={loadingTemplates || templates.length === 0}
                  >
                    <option value="">{templates.length === 0 ? 'Inga mallar tillgängliga' : 'Välj mall…'}</option>
                    {templates.map((t) => (
                      <option key={t.template_id} value={t.template_id}>
                        {t.template_name || t.name || t.template_id}
                      </option>
                    ))}
                  </Form.Select>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    Parametrar som skickas: gross, net, vat, vatRate, amount.
                  </div>
                </div>
              )}
            </div>

            {source === 'template' && loadingSuggestion && (
              <div className="text-center py-3"><Spinner animation="border" size="sm" /> Beräknar mall…</div>
            )}

            {/* ─── Manuell — single editable table (no separate preview) ─── */}
            {source === 'manual' && (
              <>
                <div className="bk-compact-table">
                  <div className="bk-compact-table-header d-flex">
                    <div style={{ flex: '3' }}>Konto</div>
                    <div style={{ flex: '1', textAlign: 'right' }}>Debet (SEK)</div>
                    <div style={{ flex: '1', textAlign: 'right' }}>Kredit (SEK)</div>
                    <div style={{ width: 32 }} />
                  </div>

                  {manualRows.map((r, idx) => (
                    <div key={idx} className="bk-compact-table-row d-flex align-items-center" style={{ gap: 8 }}>
                      <div style={{ flex: '3', minWidth: 0 }}>
                        <Select
                          options={accounts}
                          value={accounts.find((a) => a.value === String(r.account_number)) || null}
                          onChange={(opt) => updateManualRow(idx, {
                            account_number: opt ? opt.value : '',
                            // Label auto-populates from the kontoplan so the
                            // verifikat row has a readable description when
                            // written. No separate text column.
                            label: opt ? opt.accountName : '',
                          })}
                          placeholder="Välj konto…"
                          isClearable={false}
                          isSearchable
                          noOptionsMessage={() => 'Inga konton'}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (base) => ({ ...base, minHeight: 31, fontSize: '0.8125rem', borderColor: '#e2e8f0' }),
                            option: (base) => ({ ...base, fontSize: '0.8125rem', fontFamily: "'SF Mono', Monaco, monospace" }),
                            singleValue: (base) => ({ ...base, fontSize: '0.8125rem', fontFamily: "'SF Mono', Monaco, monospace" }),
                          }}
                        />
                      </div>
                      <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell">
                        <Form.Control
                          size="sm" type="number" step="0.01" min="0"
                          className="text-end"
                          value={r.debit || ''}
                          onChange={(e) => updateManualRow(idx, { debit: e.target.value === '' ? 0 : parseFloat(e.target.value), credit: 0 })}
                          style={{ fontFamily: "'SF Mono', Monaco, monospace", marginLeft: 'auto', maxWidth: 140 }}
                        />
                      </div>
                      <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell">
                        <Form.Control
                          size="sm" type="number" step="0.01" min="0"
                          className="text-end"
                          value={r.credit || ''}
                          onChange={(e) => updateManualRow(idx, { credit: e.target.value === '' ? 0 : parseFloat(e.target.value), debit: 0 })}
                          style={{ fontFamily: "'SF Mono', Monaco, monospace", marginLeft: 'auto', maxWidth: 140 }}
                        />
                      </div>
                      <div style={{ width: 32, display: 'flex', justifyContent: 'center' }}>
                        <button
                          type="button"
                          className="bk-row-delete"
                          onClick={() => removeManualRow(idx)}
                          title="Ta bort rad"
                          aria-label="Ta bort rad"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="bk-compact-table-footer d-flex">
                    <div style={{ flex: '3' }}>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        style={{ fontSize: '0.8125rem', textDecoration: 'none', fontWeight: 600 }}
                        onClick={addManualRow}
                      >
                        <Plus size={14} style={{ verticalAlign: -2, marginRight: 4 }} /> Lägg till rad
                      </button>
                    </div>
                    <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell text-success">
                      {fmtSek(manualPreview?.totals?.debit || 0)}
                    </div>
                    <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell text-primary">
                      {fmtSek(manualPreview?.totals?.credit || 0)}
                    </div>
                    <div style={{ width: 32 }} />
                  </div>
                </div>

                <div className={`bk-balance-indicator ${manualPreview?.balanced ? 'bk-balance-good' : 'bk-balance-bad'} mt-3`}>
                  {manualPreview?.balanced ? (
                    <><CheckCircle size={16} /> Debet och Kredit balanserar</>
                  ) : (
                    <><AlertTriangle size={16} /> {manualPreview?.warnings?.[0] || 'Obalanserad'}</>
                  )}
                </div>
              </>
            )}

            {source !== 'manual' && activePreview && !loadingSuggestion && (
              <>
                <div className="bk-compact-table">
                  <div className="bk-compact-table-header d-flex">
                    <div style={{ flex: '3' }}>Konto</div>
                    <div style={{ flex: '1', textAlign: 'right' }}>Debet (SEK)</div>
                    <div style={{ flex: '1', textAlign: 'right' }}>Kredit (SEK)</div>
                  </div>

                  {displayRows.map((r, idx) => (
                    <div
                      key={`${r.account || r.account_number}-${idx}`}
                      className="bk-compact-table-row d-flex align-items-center"
                    >
                      <div style={{ flex: '3', minWidth: 0 }}>
                        <div className="fw-medium" style={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: "'SF Mono', Monaco, monospace" }}>{r.account || r.account_number}</span>
                          {nameFor(r.account || r.account_number) && (
                            <span style={{ color: '#64748b' }}>— {nameFor(r.account || r.account_number)}</span>
                          )}
                          {r._editable === 'vat' && (
                            <>
                              <Form.Select
                                size="sm"
                                value={overrides.vatRate || '25'}
                                onChange={(e) => setOverrides({ ...overrides, vatRate: e.target.value })}
                                style={{ maxWidth: 92, fontSize: '0.8125rem' }}
                              >
                                <option value="25">25 %</option>
                                <option value="12">12 %</option>
                                <option value="6">6 %</option>
                                <option value="0">0 %</option>
                              </Form.Select>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                style={{ fontSize: '0.75rem', textDecoration: 'underline' }}
                                title="Beräkna moms från brutto och sats"
                                onClick={() => {
                                  const gross = Number(expense.amount) || 0;
                                  const rate = parseInt(overrides.vatRate || '25', 10) || 0;
                                  if (!gross || !rate) return;
                                  const vat = (gross * rate) / (100 + rate);
                                  setOverrides({ ...overrides, vatAmount: vat.toFixed(2), vatRate: overrides.vatRate || '25' });
                                }}
                              >
                                Beräkna från sats
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell">
                        {r._editable === 'vat' ? (
                          <Form.Control
                            type="number" step="0.01" min="0" size="sm"
                            placeholder="0,00"
                            value={overrides.vatAmount || ''}
                            onChange={(e) => setOverrides({ ...overrides, vatAmount: e.target.value })}
                            style={{
                              textAlign: 'right',
                              fontFamily: "'SF Mono', Monaco, monospace",
                              maxWidth: 140, marginLeft: 'auto',
                            }}
                          />
                        ) : r.debit > 0 ? (
                          <span className="fw-medium text-success">{fmtSek(r.debit)}</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </div>
                      <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell">
                        {r.credit > 0 ? (
                          <span className="fw-medium text-primary">{fmtSek(r.credit)}</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </div>
                    </div>
                  ))}

                  <div className="bk-compact-table-footer d-flex">
                    <div style={{ flex: '3' }}>Summa</div>
                    <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell text-success">
                      {fmtSek(activePreview.totals.debit)}
                    </div>
                    <div style={{ flex: '1', textAlign: 'right' }} className="bk-amount-cell text-primary">
                      {fmtSek(activePreview.totals.credit)}
                    </div>
                  </div>
                </div>

                <div className={`bk-balance-indicator ${activePreview.balanced ? 'bk-balance-good' : 'bk-balance-bad'} mt-3`}>
                  {activePreview.balanced ? (
                    <><CheckCircle size={16} /> Debet och Kredit balanserar</>
                  ) : (
                    <><AlertTriangle size={16} /> Obalanserad — går ej att bokföra</>
                  )}
                </div>

                {activePreview.warnings && activePreview.warnings.length > 0 && (
                  <Alert variant="warning" className="mt-3" style={{ fontSize: 12 }}>
                    <AlertTriangle size={14} className="me-1" />
                    {activePreview.warnings.map((w, i) => <div key={i}>{w}</div>)}
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onClose} disabled={busy}>Avbryt</Button>
        <Button variant="primary" onClick={handleBook} disabled={!canBook || busy}>
          {busy ? <Spinner size="sm" animation="border" /> : <><CheckCircle size={14} className="me-1" /> Bokför</>}
        </Button>
      </Modal.Footer>

      <style>{verifikatTableCss}</style>
    </Modal>
  );
};

export default BookExpenseModal;
